import { useEffect, useState } from 'react';
import type { AssetSearchResult } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { Button, FormSearchInput } from '@/components/UI';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { InstrumentDisplay } from './callReviewModel';

type InstrumentPickerProps = {
    display: InstrumentDisplay | undefined;
    onPick: (result: AssetSearchResult) => void;
    /** Opens the search straight away (a call added by hand has no instrument yet). */
    startOpen?: boolean;
};

const SEARCH_DEBOUNCE_MS = 300;

/** Shows the call's instrument and lets the reviewer pick another by symbol or name. */
const InstrumentPicker = ({ display, onPick, startOpen = false }: InstrumentPickerProps) => {
    const [open, setOpen] = useState(startOpen);
    const [input, setInput] = useState('');
    const [term, setTerm] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setTerm(input.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [input]);

    const search = useAppQuery(
        { ...trpc.search.search.queryOptions({ term }), enabled: open && term.length > 0, staleTime: 30_000 },
        { fallbackErrorMessage: 'Instrument search failed', showErrorToast: true }
    );

    if (!open) {
        return (
            <div className="flex items-center gap-3">
                <span className="font-semibold text-slate-100">{display ? display.symbol : 'Instrument not chosen'}</span>
                {display?.name && <span className="text-sm text-slate-400">{display.name}</span>}
                <Button onClick={() => setOpen(true)} size="sm" variant="secondary">
                    Change
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <FormSearchInput
                    aria-label="Search instruments"
                    autoFocus
                    onChange={(event) => setInput(event.target.value)}
                    placeholder="Symbol or company name"
                    value={input}
                />
                <Button onClick={() => setOpen(false)} size="sm" variant="secondary">
                    Cancel
                </Button>
            </div>
            {term && (
                <ul className="max-h-56 overflow-y-auto rounded border border-slate-700 bg-slate-900">
                    {search.isLoading && <li className="px-3 py-2 text-sm text-slate-400">Searching…</li>}
                    {search.data?.results.length === 0 && <li className="px-3 py-2 text-sm text-slate-400">No matches</li>}
                    {search.data?.results.map((result) => (
                        <li key={`${result.type}:${result.id}`}>
                            <button
                                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-800"
                                onClick={() => {
                                    onPick(result);
                                    setOpen(false);
                                    setInput('');
                                }}
                                type="button"
                            >
                                <span className="font-semibold">{result.symbol}</span> <span className="text-slate-400">{result.name}</span>{' '}
                                <span className="text-xs text-slate-500">{result.type}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default InstrumentPicker;
